import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ReactComponent as DropdownIcon } from "../../assets/images/icons/logs/dropdown-arrow.svg";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import ButtonOne from "../../common/normal-button";
import TableSearchInput from "../TableSearchInput";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import { useAuth } from "../../hooks/AuthProvider";
import useLogExport from "../../hooks/useLogExport";
import { LEAD_GEN_CUSTOMERS, LEAD_GEN_CUSTOMERS_EXPORT_PREVIEW, LEAD_GEN_CUSTOMERS_EXPORT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_ROWS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

// ── Component ─────────────────────────────────────────────────────────────────

const CustomerAccounts = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [customers, setCustomers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ message: "", severity: "error" });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [charCount, setCharCount] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const [filterSource, setFilterSource] = useState("");
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);

  const [detailDialog, setDetailDialog] = useState({ open: false, customer: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [deleting, setDeleting] = useState(false);

  // Per-row overflow menu
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const handleActionClick = (e, row) => {
    e.stopPropagation();
    setActionMenuAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };
  const handleActionClose = () => setActionMenuAnchorEl(null);

  // ── Debounce search input ────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Fetch source filter options ──────────────────────────────────────────

  useEffect(() => {
    const fetchSources = async () => {
      setSourcesLoading(true);
      try {
        const token = await getValidToken();
        const res = await fetch(`${LEAD_GEN_CUSTOMERS}sources/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSourceOptions(Array.isArray(data) ? data : []);
      } catch {
        // silently fail — source filter just shows no options
      } finally {
        setSourcesLoading(false);
      }
    };
    fetchSources();
  }, []);

  // ── Build query params (shared between list and export) ─────────────────

  const buildParams = (includePagination = true) => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (filterSource) params.set("source", filterSource);
    if (includePagination) {
      params.set("limit", rowsPerPage);
      params.set("offset", page * rowsPerPage);
    }
    return params;
  };

  // ── Fetch customers ───────────────────────────────────────────────────────

  const fetchCustomers = async () => {
    setLoading(true);
    setAlert({ message: "", severity: "error" });
    try {
      const token = await getValidToken();
      const res = await fetch(`${LEAD_GEN_CUSTOMERS}?${buildParams(true).toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch customers");
      const data = await res.json();
      setCustomers(data.results || []);
      setCount(data.count || 0);
    } catch (err) {
      setAlert({ message: err.message || "Failed to fetch customers", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filterSource]);

  // ── Filters ───────────────────────────────────────────────────────────────

  const handleResetFilters = () => {
    setFilterSource("");
    setPage(0);
  };

  const handleSearchChange = (value) => {
    if (value.length > 50) return;
    setSearch(value);
    setCharCount(value.length);
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const exportFilters = useMemo(
    () => ({ search: debouncedSearch, source: filterSource }),
    [debouncedSearch, filterSource],
  );

  const {
    preview: exportPreview,
    jobStatus: exportJobStatus,
    loading: exporting,
    error: exportError,
    triggerExport,
  } = useLogExport(exportFilters, {
    previewEndpoint: LEAD_GEN_CUSTOMERS_EXPORT_PREVIEW,
    exportEndpoint: LEAD_GEN_CUSTOMERS_EXPORT,
    fallbackFilenamePrefix: "Customers",
  });

  // Surface export failures only once an export attempt finishes, so a failed
  // background preview fetch doesn't raise an alert on page load.
  const wasExportingRef = useRef(false);
  useEffect(() => {
    if (wasExportingRef.current && !exporting) {
      if (exportError) {
        setAlert({ message: exportError, severity: "error" });
      } else if (exportJobStatus?.status === "ready") {
        setAlert({ message: "Your export is ready. The download link has been sent to your email.", severity: "success" });
      }
    }
    wasExportingRef.current = exporting;
  }, [exporting, exportError, exportJobStatus]);

  const handleExport = () => {
    if (!exportPreview) {
      setAlert({ message: "Still calculating export size, please try again in a moment.", severity: "warning" });
      return;
    }
    if (exportPreview.export_mode === "blocked") {
      setAlert({ message: exportPreview.message, severity: "error" });
      return;
    }
    setAlert(
      exportPreview.export_mode === "async"
        ? { message: exportPreview.message, severity: "info" }
        : { message: "", severity: "error" },
    );
    triggerExport();
  };

  // ── Pagination ────────────────────────────────────────────────────────────

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // ── View / Delete ─────────────────────────────────────────────────────────

  const handleViewDetails = (row) => {
    handleActionClose();
    setDetailDialog({ open: true, customer: row });
  };
  const handleDetailClose = () => setDetailDialog({ open: false, customer: null });

  const handleDeleteClick = (row) => {
    handleActionClose();
    setDeleteDialog({ open: true, customer: row });
  };
  const handleDeleteConfirm = async () => {
    const { customer } = deleteDialog;
    if (!customer) return;
    setDeleting(true);
    try {
      const token = await getValidToken();
      const res = await fetch(`${LEAD_GEN_CUSTOMERS}${customer.id}/delete/`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || "Failed to delete customer");
      }
      setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
      setCount((prev) => Math.max(0, prev - 1));
      setDeleteDialog({ open: false, customer: null });
    } catch (err) {
      setAlert({ message: err.message || "Failed to delete customer", severity: "error" });
      setDeleteDialog({ open: false, customer: null });
    } finally {
      setDeleting(false);
    }
  };

  // ── Table columns ─────────────────────────────────────────────────────────

  const textCell = (value) =>
    value !== null && value !== undefined && value !== "" ? (
      <Typography sx={{ ...typography.bodySmall, color: "#1D2330" }}>{value}</Typography>
    ) : (
      <Typography component="span" sx={{ ...typography.bodySmall, color: "#9CA3AF", fontStyle: "italic" }}>
        —
      </Typography>
    );

  const truncatedCell = (value, maxWidth = 220) =>
    value ? (
      <Tooltip title={value} arrow placement="top">
        <Typography
          sx={{
            ...typography.bodySmall,
            color: "#1D2330",
            maxWidth,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ) : (
      textCell(null)
    );

  const columns = useMemo(
    () => [
      { id: "customer_name", label: "Customer Name", minWidth: 180, render: (row) => textCell(row.customer_name) },
      {
        id: "website",
        label: "Website",
        minWidth: 170,
        render: (row) =>
          row.website ? (
            <Typography
              component="a"
              href={row.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ ...typography.bodySmall, color: ColorsConstants.secondaryBackgroundColor, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              {row.website}
            </Typography>
          ) : (
            textCell(null)
          ),
      },
      { id: "phone", label: "Phone", minWidth: 140, render: (row) => textCell(row.phone) },
      { id: "industry", label: "Industry", minWidth: 140, render: (row) => textCell(row.industry) },
      { id: "annual_revenue", label: "Annual Revenue", minWidth: 150, render: (row) => textCell(row.annual_revenue) },
      { id: "employees", label: "Employees", minWidth: 110, render: (row) => textCell(row.employees) },
      { id: "location", label: "Location", minWidth: 150, render: (row) => textCell(row.location) },
      {
        id: "source",
        label: "Source",
        minWidth: 150,
        render: (row) =>
          row.source ? (
            <AppChip label={row.source} sx={{ bgcolor: "#F0F5FF", color: ColorsConstants.primaryBackgroundColor }} />
          ) : (
            textCell(null)
          ),
      },
      { id: "owner", label: "Owner", minWidth: 140, render: (row) => textCell(row.owner) },
      { id: "description", label: "Description", minWidth: 220, render: (row) => truncatedCell(row.description) },
      { id: "last_synced_at", label: "Last Synced", minWidth: 180, render: (row) => textCell(formatWithTzLabel(row.last_synced_at_iso)) },
      {
        id: "actions",
        label: "Actions",
        minWidth: 70,
        align: "center",
        render: (row) => (
          <IconButton
            size="small"
            onClick={(e) => handleActionClick(e, row)}
            sx={{ color: "#6C7C93", "&:hover": { bgcolor: "#F0F2F8", color: ColorsConstants.primaryBackgroundColor } }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>
        ),
      },
    ],
    [],
  );

  const rowSx = (_, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    height: "47px",
  });

  const sourceMenuItems = sourceOptions.map((s) => ({ label: s, value: s }));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ backgroundColor: "#FAFAFA", p: 3 }}>

      {/* ── Toolbar ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{ ...typography.h1, color: "#1D2330" }}>Customers</Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0, ml: "auto" }}>
          <ButtonOne
            variant="outlined"
            onClick={handleExport}
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <FileDownloadOutlinedIcon />}
            title="Export"
          />
        </Box>
      </Box>

      {/* ── Alert ── */}
      {alert.message && (
        <Alert severity={alert.severity} onClose={() => setAlert({ message: "", severity: "error" })} sx={{ mb: 2, borderRadius: "8px" }}>
          {alert.message}
        </Alert>
      )}

      {/* ── Filter bar ── */}
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2, overflowX: "auto" }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            charCount={charCount}
            placeholder="Search by name, phone…"
            tooltip="Searches by customer name and phone number"
          />
        </Box>

        <AppDropdown
          endIcon={<DropdownIcon />}
          menuItems={sourceMenuItems}
          selectedValue={filterSource}
          loading={sourcesLoading}
          emptyText="No sources yet"
          onMenuSelect={(value) => { setFilterSource(filterSource === value ? "" : value); setPage(0); }}
          sx={{ whiteSpace: "nowrap", minWidth: 140, px: 1.2 }}
        >
          Source
        </AppDropdown>

        {filterSource && (
          <ButtonOne
            variant="outlined"
            onClick={handleResetFilters}
            sx={{
              height: 36, px: 2, ...typography.bodyLarge,
              border: "1px solid #DCDFE5", color: "#6C7C93",
              "&:hover": { border: "1px solid #9CA3AF", backgroundColor: "transparent" },
            }}
          >
            Reset
          </ButtonOne>
        )}
      </Box>

      {/* ── Table ── */}
      <AppTable
        columns={columns}
        rows={customers}
        rowKey="id"
        loading={loading}
        onRowClick={handleViewDetails}
        maxHeight={560}
        rowSx={rowSx}
        paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
        emptyMessage="No customers found"
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* ── Row overflow menu ── */}
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleActionClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem onClick={() => handleViewDetails(selectedRow)} sx={{ ...typography.bodyLarge, gap: 1 }}>
          <VisibilityOutlinedIcon sx={{ fontSize: 18, color: "#6C7C93" }} /> View Details
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(selectedRow)} sx={{ ...typography.bodyLarge, gap: 1, color: "#E05252" }}>
          <DeleteOutlineIcon sx={{ fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ── Detail Dialog ── */}
      <Dialog open={detailDialog.open} onClose={handleDetailClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1, ...typography.h2, color: "#1D2330" }}>
          Customer Details
          <IconButton size="small" onClick={handleDetailClose} sx={{ color: "#6C7C93" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          {detailDialog.customer && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, backgroundColor: "#F8FAFC", borderRadius: "8px", p: 2 }}>
              {[
                ["Customer Name", detailDialog.customer.customer_name],
                ["Website", detailDialog.customer.website],
                ["Phone", detailDialog.customer.phone],
                ["Industry", detailDialog.customer.industry],
                ["Annual Revenue", detailDialog.customer.annual_revenue],
                ["Employees", detailDialog.customer.employees],
                ["Location", detailDialog.customer.location],
                ["Source", detailDialog.customer.source],
                ["Owner", detailDialog.customer.owner],
                ["Last Synced", detailDialog.customer.last_synced_at_iso ? formatWithTzLabel(detailDialog.customer.last_synced_at_iso) : null],
                ["Received At", detailDialog.customer.created_at_iso ? formatWithTzLabel(detailDialog.customer.created_at_iso) : null],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mb: 0.25 }}>{label}</Typography>
                  <Typography sx={{ ...typography.bodyLarge, color: "#1D2330" }}>{value || value === 0 ? value : "—"}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, customer: null })}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 360, p: 1 } }}
      >
        <DialogTitle sx={{ ...typography.h2, color: "#1D2330", pb: 1 }}>Delete Customer</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ ...typography.bodyLarge, color: "#6C7C93" }}>
            Are you sure you want to delete this customer? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <ButtonOne
            variant="outlined"
            onClick={() => setDeleteDialog({ open: false, customer: null })}
            disabled={deleting}
            sx={{ height: 36, px: 2, ...typography.bodyLarge, border: "1px solid #DCDFE5", color: "#1D2330", "&:hover": { border: "1px solid #9CA3AF", backgroundColor: "transparent" } }}
          >
            Cancel
          </ButtonOne>
          <ButtonOne
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ height: 36, px: 2, ...typography.bodyLarge, backgroundColor: "#E05252", "&:hover": { backgroundColor: "#C94444" }, "&.Mui-disabled": { backgroundColor: "#E05252", opacity: 0.6 } }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </ButtonOne>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerAccounts;
